import { useSyncExternalStore } from 'react';
import type { Event } from '../types';
import { emptySnapshot, floorsOf, isSnap, listStore, localDay, snapOf, type EventSnap } from './saved';

/**
 * RECENTLY VIEWED, ON THIS DEVICE (2026-10-19): the last 12 event pages this browser opened
 * (localStorage 'tixinfo.recent.v1', newest first). Shows that are over drop off. Written by the
 * event page (src/components/SaveButton.tsx HeroActions), never by search results.
 *
 * Each entry keeps the fees-included floor it recorded that day, so the next visit can say "down $X
 * since you looked" (same rules as saved prices: src/lib/saved.ts, shared/saved-price.mjs) — only
 * when that floor was also the one on screen (`seen`), never a built price the live refresh hid.
 */
export interface RecentItem extends EventSnap {
  /** fees-included floor on that view (USD), null when none included fees */
  floorAtView: number | null;
  /** before-fees floor, only when nothing included fees */
  stickerAtView: number | null;
  /** taken without HelloTickets (a visitor outside the US) */
  intl: boolean;
  /** that floor was the one the page showed (the live refresh had answered and agreed); missing = no */
  seen?: boolean;
  /** the visitor's local date of that view, YYYY-MM-DD */
  viewedAt: string;
}

const RECENT_KEY = 'tixinfo.recent.v1';
export const RECENT_MAX = 12;

const num = (v: unknown) => v == null || (typeof v === 'number' && Number.isFinite(v) && v > 0);
const isRecentItem = (v: unknown): v is RecentItem => {
  const o = v as Record<string, unknown>;
  return isSnap(v) && num(o.floorAtView) && num(o.stickerAtView) && typeof o.viewedAt === 'string' && (o.seen === undefined || typeof o.seen === 'boolean');
};

export const recentStore = listStore<RecentItem>(RECENT_KEY, RECENT_MAX, isRecentItem, (r) => r.date >= localDay());

/** This device's recently viewed events, newest first: [] on the server and until hydration is done. */
export const useRecent = (): RecentItem[] => useSyncExternalStore(recentStore.subscribe, recentStore.get, emptySnapshot);

/** Record a view of this event; returns the entry it replaced (the previous view), if any. */
export const recordView = (e: Event, intl: boolean, seen: boolean): RecentItem | null => {
  const today = localDay();
  const list = recentStore.get();
  const prev = list.find((r) => r.id === e.id) ?? null;
  const { floor, sticker } = floorsOf(e, intl);
  const item: RecentItem = { ...snapOf(e), floorAtView: floor, stickerAtView: floor === null ? sticker : null, intl, seen, viewedAt: today };
  recentStore.set([item, ...list.filter((r) => r.id !== e.id && r.date >= today)]);
  return prev;
};

/** The view before this page life's first one, per event: what "since you looked" compares with. */
const before = new Map<string, RecentItem | null>();

/**
 * Record that the event page is open (src/components/SaveButton.tsx HeroActions, again whenever
 * `seen` becomes known): returns the visit before this page life's first view.
 */
export const noteView = (e: Event, intl: boolean, seen: boolean): RecentItem | null => {
  const prev = recordView(e, intl, seen);
  if (!before.has(e.id)) before.set(e.id, prev);
  return before.get(e.id) ?? null;
};

/**
 * The visit before this page life's first view of the event, without recording one (the "since you
 * looked" line): the same answer as noteView, whichever of the two runs first.
 */
export const lastVisit = (id: string): RecentItem | null => {
  if (!before.has(id)) before.set(id, recentStore.get().find((r) => r.id === id) ?? null);
  return before.get(id) ?? null;
};

/** The key src/lib/personal.ts keeps the viewing profile under (categories, cities, artists). */
const AFFINITY_KEY = 'tixinfo.affinity.v1';
/** This tab's last partner click (src/components/WelcomeBack.tsx). */
const LAST_OUT_KEY = 'tixinfo.lastOut';

/**
 * "Clear my history": recently viewed, the viewing profile behind the homepage's ordering and this
 * tab's last partner click. Saved events stay. Returns an undo (for the lists), or null when this
 * browser keeps nothing anyway.
 */
export const clearHistory = (): (() => void) | null => {
  const list = recentStore.get();
  let profile: string | null = null;
  try {
    profile = localStorage.getItem(AFFINITY_KEY);
    localStorage.removeItem(AFFINITY_KEY);
    sessionStorage.removeItem(LAST_OUT_KEY);
  } catch {
    /* nothing stored, nothing to clear */
  }
  if (!recentStore.set([])) return null;
  before.clear();
  return () => {
    recentStore.set(list);
    try {
      if (profile !== null) localStorage.setItem(AFFINITY_KEY, profile);
    } catch {
      /* the profile simply starts over */
    }
  };
};
